import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { ResponsiveAutocomplete } from '@/components/selects/ResponsiveAutocomplete';
import { toInsightList, useListInsightFieldsQuery } from '../../api/insightFieldsApi';

/**
 * Pick an Insight id (a sales team, service team, account type or authorised rep) from
 * `ledger`'s Insight list for `fieldType` (listInsightFields), searchable by name. value /
 * onChange are the id (a number), or null.
 *
 * - Inactive values are shown but can't be picked; hovering one says "Inactive".
 * - An id that's set but isn't in the list still shows, as "<id> (not in Insight)", so
 *   the admin can see what to change.
 * - Without a ledger (no primary broker group yet) it's disabled. If Insight can't be read,
 *   a set id shows as it is, and the empty dropdown says why (the ledger's Insight doesn't
 *   offer the list, or it couldn't be loaded).
 * Other props go to ResponsiveAutocomplete (e.g. error).
 */
export const AdminInsightIdPicker = ({ ledger, fieldType, value, onChange, ...props }) => {
	// currentData, not data, so another ledger's list isn't offered while this one loads, or
	// if it fails
	const { currentData: options = [], isFetching, isError, error } = useListInsightFieldsQuery(
		{ ledger, fieldType },
		{ skip: !ledger },
	);
	const { isNotAvailable } = toInsightList({ data: options, isError, error });
	const isListed = options.some(({ id }) => id === value);
	// When Insight couldn't be read, the id is shown as it is rather than as missing
	const isMissing = value != null && !isFetching && !isError && !isListed;
	const allOptions = [
		...(isMissing ? [{ id: value, name: `${value} (not in Insight)`, isInactive: true }] : []),
		...(value != null && isError ? [{ id: value, name: String(value) }] : []),
		...options,
	];

	return (
		<ResponsiveAutocomplete
			options={allOptions}
			value={value}
			onChange={(id) => onChange(id ?? null)}
			isOptionDisabled={(option) => option.isInactive}
			renderOptionContent={(option) =>
				option.isInactive && !(isMissing && option.id === value) ? (
					<Tooltip title='Inactive' placement='left'>
						{/* A disabled option gets no pointer events; this span does, for the
						    tooltip, and stops the click so it isn't picked */}
						<Box
							component='span'
							sx={{ pointerEvents: 'auto' }}
							onClick={(event) => event.stopPropagation()}
						>
							{option.name}
						</Box>
					</Tooltip>
				) : (
					option.name
				)
			}
			searchable
			fitMenuToContent
			loading={isFetching}
			noOptionsText={
				(isNotAvailable && 'This ledger\'s Insight doesn\'t offer this list') ||
				(isError && 'Couldn\'t load this list from Insight') ||
				'No options'
			}
			disabled={!ledger}
			{...props}
		/>
	);
};

AdminInsightIdPicker.displayName = 'AdminInsightIdPicker';
